import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { formatLocalCalendarYmd, parseDateOnly } from '../VenueDashboard/utils/dateUtils';
import { FigmaChevronDownIcon } from '../icons';
import './CustomDatePicker.css';

const CustomDatePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select date",
  disabled = false,
  className = "",
  dropdownClassName = "",
  matchInputWidth = false,
  id = "",
  minDate = null,
  mobileSheet = false,
  alignHeaderToGrid = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || '');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 767px)').matches
  );
  const asSheet = mobileSheet && isNarrow;

  useEffect(() => {
    if (!mobileSheet || !window.matchMedia) return undefined;
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setIsNarrow(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [mobileSheet]);

  // Generate calendar dates for the current month
  const generateCalendarDates = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const dates = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      dates.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      dates.push({
        day,
        // Never use toISOString() for calendar YMD — in zones ahead of UTC, local midnight is the previous UTC day.
        date: formatLocalCalendarYmd(date),
        fullDate: date
      });
    }
    
    return dates;
  };

  const calendarDates = generateCalendarDates();

  // Handle date selection
  const handleDateSelect = (dateStr) => {
    if (!dateStr) return;
    
    setSelectedDate(dateStr);
    onChange(dateStr);
    setIsOpen(false);
  };

  // Handle month navigation
  const handleMonthChange = (direction) => {
    if (direction === 'next') {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(prev => prev + 1);
      } else {
        setCurrentMonth(prev => prev + 1);
      }
    } else {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(prev => prev - 1);
      } else {
        setCurrentMonth(prev => prev - 1);
      }
    }
  };

  // Check if date is disabled (before minDate)
  const isDateDisabled = (dateYmd) => {
    if (!minDate) return false;
    const min = parseDateOnly(String(minDate).split('T')[0]);
    const checkDate = parseDateOnly(String(dateYmd).split('T')[0]);
    if (!min || !checkDate) return false;
    return checkDate < min;
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return placeholder;
    try {
      const date = parseDateOnly(String(dateStr).split('T')[0]);
      if (!date) return dateStr;
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Calculate dropdown position when opening (fixed positioning = viewport coords)
  const calculateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const dropdownHeight = 350;
      const dropdownWidth = matchInputWidth ? rect.width : 280;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;

      let top = rect.bottom + 4;
      let left = rect.left;

      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        top = rect.top - dropdownHeight - 4;
      }

      const spaceRight = viewportWidth - rect.left;
      if (spaceRight < dropdownWidth) {
        left = Math.max(8, viewportWidth - dropdownWidth - 10);
      }

      setDropdownPosition({ top, left, width: matchInputWidth ? rect.width : undefined });
    }
  }, [matchInputWidth]);

  // Handle opening dropdown
  const handleToggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (newIsOpen) {
        // Calculate position after state update
        setTimeout(() => {
          calculateDropdownPosition();
        }, 0);
      }
    }
  };

  useCloseOnEscape(isOpen, () => setIsOpen(false));

  // Line the month label and chevrons up with the calendar grid ("Sun"/"Sat"),
  // or, in the mobile sheet, with the sheet title and close icon.
  useLayoutEffect(() => {
    if (!isOpen || !alignHeaderToGrid) return;
    const dropdown = dropdownRef.current;
    const header = dropdown?.querySelector('.date-picker-header');
    const weekdays = dropdown?.querySelectorAll('.weekday');
    const month = header?.querySelector('.month-year-display');
    const navPaths = header?.querySelectorAll('.month-nav-btn path');
    if (!header || !weekdays || weekdays.length < 7 || !month || !navPaths?.length) return;

    const textRect = (el) => {
      const range = document.createRange();
      range.selectNodeContents(el);
      return range.getBoundingClientRect();
    };
    header.style.paddingLeft = '';
    header.style.paddingRight = '';
    const styles = window.getComputedStyle(header);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    const sheetTitle = asSheet ? dropdown.querySelector('.date-picker-sheet-title') : null;
    const closePath = asSheet ? dropdown.querySelector('.date-picker-sheet-close path') : null;
    const targetLeft = sheetTitle ? textRect(sheetTitle).left : textRect(weekdays[0]).left;
    const targetRight = closePath
      ? closePath.getBoundingClientRect().right
      : textRect(weekdays[weekdays.length - 1]).right;
    const monthLeft = textRect(month).left;
    const chevronRight = navPaths[navPaths.length - 1].getBoundingClientRect().right;
    if (!targetLeft || !targetRight) return;
    header.style.paddingLeft = `${Math.max(0, padLeft + (targetLeft - monthLeft))}px`;
    header.style.paddingRight = `${Math.max(0, padRight + (chevronRight - targetRight))}px`;
  }, [isOpen, alignHeaderToGrid, asSheet, dropdownPosition, currentMonth, currentYear]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Calculate position when dropdown opens
      calculateDropdownPosition();
      
      document.addEventListener('mousedown', handleClickOutside);
      // Recalculate position on scroll or resize
      const handleScroll = () => calculateDropdownPosition();
      const handleResize = () => calculateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, calculateDropdownPosition]);

  // Update selected date when value prop changes
  useEffect(() => {
    setSelectedDate(value || '');
    if (value) {
      const date = parseDateOnly(String(value).split('T')[0]);
      if (date) {
        setCurrentMonth(date.getMonth());
        setCurrentYear(date.getFullYear());
      }
    }
  }, [value]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`custom-date-picker ${className} ${!selectedDate ? 'placeholder' : ''}`}>
      <div 
        ref={inputRef}
        className={`date-picker-input ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggleDropdown}
        id={id}
      >
        <span className="date-display">
          {formatDisplayDate(selectedDate)}
        </span>
        <FigmaChevronDownIcon className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`} />
      </div>
      
      {isOpen &&
        createPortal(
          <>
          {asSheet ? (
            <div className="date-picker-sheet-backdrop" aria-hidden onClick={() => setIsOpen(false)} />
          ) : null}
          <div
            ref={dropdownRef}
            className={`date-picker-dropdown ${dropdownClassName} ${asSheet ? 'date-picker-dropdown--sheet' : ''}`.trim()}
            role={asSheet ? 'dialog' : undefined}
            aria-modal={asSheet ? 'true' : undefined}
            aria-label={asSheet ? 'Choose a date' : undefined}
            style={
              asSheet
                ? undefined
                : {
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    ...(dropdownPosition.width
                      ? { width: `${dropdownPosition.width}px`, minWidth: 0, maxWidth: 'none' }
                      : {}),
                    zIndex: 10000,
                  }
            }
          >
            {asSheet ? (
              <>
                <div className="date-picker-sheet-handle" aria-hidden />
                <div className="date-picker-sheet-head">
                  <p className="date-picker-sheet-title">Choose a date</p>
                  <button type="button" className="date-picker-sheet-close" aria-label="Close" onClick={() => setIsOpen(false)}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </>
            ) : null}
            <div className="date-picker-header">
              <button
                type="button"
                className="month-nav-btn"
                onClick={() => handleMonthChange('prev')}
                aria-label="Previous month"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span className="month-year-display">
                {monthNames[currentMonth]} {currentYear}
              </span>
              <button
                type="button"
                className="month-nav-btn"
                onClick={() => handleMonthChange('next')}
                aria-label="Next month"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="date-picker-calendar">
              <div className="calendar-weekdays">
                {dayNames.map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>

              <div className="calendar-days">
                {calendarDates.map((dateObj, index) => {
                  if (!dateObj) {
                    return <div key={`empty-${index}`} className="calendar-day empty"></div>;
                  }

                  const isSelected = selectedDate === dateObj.date;
                  const isDisabled = isDateDisabled(dateObj.date);

                  return (
                    <div
                      key={dateObj.date}
                      className={`calendar-day ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => !isDisabled && handleDateSelect(dateObj.date)}
                    >
                      {dateObj.day}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default CustomDatePicker;

